<template>
  <div
    ref="mediaGalleryOverlay"
    :class="['owc-media-gallery-overlay', { 'owc-media-gallery-overlay--dark': isDarkMode }]"
  >
    <div class="owc-media-gallery-overlay__wrapper">
      <!-- TODO: i18n for aria-label -->
      <button
        v-if="isScreenMaxVisible && !isMaximized"
        aria-label="zoom in overlay image"
        :class="[
          'owc-media-gallery-overlay__btn',
          'owc-media-gallery-overlay__btn--screen-max',
          'wb-round-button',
          'wb-round-button--medium',
          { 'wb-round-button--theme-dark': isDarkMode },
          roundButtonClass,
        ]"
        @click="toggleMaximize"
      >
        <wb-icon class="wb-round-button__icon" aria-hidden="true" name="screen-max"></wb-icon>
      </button>

      <!-- TODO: i18n for aria-label -->
      <button
        v-if="isMaximized"
        aria-label="go back"
        :class="[
          'owc-media-gallery-overlay__btn',
          'owc-media-gallery-overlay__btn--back',
          'wb-round-button',
          'wb-round-button--medium',
          { 'wb-round-button--theme-dark': isDarkMode },
          roundButtonClass,
        ]"
        @click="onBackClick"
      >
        <wb-icon class="wb-round-button__icon" aria-hidden="true" name="chevron-left"></wb-icon>
      </button>
      <!-- TODO: i18n for aria-label -->
      <button
        v-if="showCloseButton"
        aria-label="close overlay"
        :class="[
          'owc-media-gallery-overlay__btn',
          'owc-media-gallery-overlay__btn--close',
          'wb-round-button',
          'wb-round-button--medium',
          { 'wb-round-button--theme-dark': isDarkMode },
          roundButtonClass,
        ]"
        @click="onCloseClick"
      >
        <wb-icon class="wb-round-button__icon" aria-hidden="true" name="close"></wb-icon>
      </button>

      <!-- CONTENT WRAP: wraps content AND controls -->
      <div class="owc-media-gallery-overlay__content-wrapper">
        <!-- CONTROLS: contains arrows and mock image for sizing and positioning -->
        <div class="owc-media-gallery-overlay__controls">
          <!-- TODO: i18n for aria-label -->
          <button
            @click="prevSlide"
            aria-label="go back to previous slide"
            :disabled="isFirstSlide"
            :class="[
              { 'wb-round-button--theme-dark': isDarkMode },
              'owc-media-gallery-overlay__btn',
              'owc-media-gallery-overlay__btn--prev',
              'wb-round-button',
              'wb-round-button--medium',
              'wb-round-button--solid-level-0',
            ]"
          >
            <wb-icon class="wb-round-button__icon" aria-hidden="true" name="chevron-left"></wb-icon>
          </button>
          <div class="owc-media-gallery-overlay__controls-mock-image"></div>
          <!-- TODO: i18n for aria-label -->
          <button
            @click="nextSlide"
            aria-label="go to the next slide"
            :disabled="isLastSlide"
            :class="[
              { 'wb-round-button--theme-dark': isDarkMode },
              'owc-media-gallery-overlay__btn',
              'owc-media-gallery-overlay__btn--next',
              'wb-round-button',
              'wb-round-button--medium',
              'wb-round-button--solid-level-0',
            ]"
          >
            <wb-icon class="wb-round-button__icon" aria-hidden="true" name="chevron-right"></wb-icon>
          </button>
        </div>

        <!-- CONTENT -->
        <div class="owc-media-gallery-overlay__content" ref="overlayContent">
          <wb-slider
            v-show="!isMaximized"
            hide-arrow-navigation
            hide-dot-navigation
            pause-auto-rotate
            :initial-item="galleryItemIndex + 1"
            class="owc-media-gallery-overlay__slider"
            @wbchange="carouselSlideChanged"
            ref="overlaySlider"
          >
            <MediaGalleryCarouselItemComponent
              v-for="(item, index) in galleryItems"
              class="wb-slider__item wb-slider__item--full-width"
              :class="{
                'owc-media-gallery-overlay__current': index === currentSlide,
              }"
              :key="index"
              :item="item"
              :position="index"
            />
          </wb-slider>
          <wb-aspect-ratio
            v-show="isMaximized"
            class="owc-media-gallery-overlay__zoomed-image"
            :ratio="ratio"
            ref="overlayMaximize"
            :isMaximized="isMaximized"
          >
            <img :src="maximizedSrc" alt="" />
          </wb-aspect-ratio>
          <template v-if="isMaximized && !isOverlayClosed">
            <overlay
              class="owc-media-gallery-overlay__overlay"
              :isDarkMode="true"
              @overlayClose="overlayCloseHandler"
              @touchstart="overlayCloseHandler"
            >
              <div class="owc-media-gallery-overlay__hint">
                <div class="owc-media-gallery-overlay__icon" v-html="OverlayPinchIcon"></div>
                <p class="wb-type-copy-secondary">{{ overlayPinchLabel }}</p>
              </div>
            </overlay>
          </template>
        </div>
      </div>

      <MediaGalleryCarouselThumbnails
        class="owc-media-gallery-overlay__thumbnails"
        v-if="!isMaximized"
        :thumbnails="galleryItems"
        v-model="currentSlide"
        @updateCurrentItem="updateCurrentItem"
      />
      <div
        v-if="!isMaximized"
        :class="[
          'owc-media-gallery-overlay__dot-navigation',
          {
            'owc-media-gallery-overlay__dot-navigation--stick-to-bottom': isNavigationDotsSticky,
          },
        ]"
        ref="overlayNavigationDots"
      >
        <wb-dot-navigation
          :theme="isDarkMode ? 'dark' : 'light'"
          :pages="galleryItems.length"
          :current-page="currentSlide + 1"
          @wbchange="onDotNavigationChanged"
        ></wb-dot-navigation>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import Vue, { VueConstructor } from 'vue';
import { debounce } from '@owc-utils/Events';
import MediaGalleryCarouselItemComponent from './MediaGalleryCarouselItem.vue';
import MediaGalleryCarouselThumbnails from './MediaGalleryCarouselThumbnails.vue';
import {
  MediaGalleryOverlayData,
  MediaGalleryOverlayRefs,
  MediaGalleryOverlayComputed,
  MediaGalleryOverlayProps,
  MediaGalleryOverlayMethods,
  MediaGalleryCarouselVideo,
  MediaGalleryCarouselItem,
} from './MediaGalleryOverlay';
import { ScrollLockService } from '@workbench/core';
import { MediaQuery } from '@owc-utils/MediaQueryService/MediaQuery';
import { MediaQueryChangeEvent } from '@owc-utils/MediaQueryService/MediaQueryChangeEvent';
import { createEventListener } from '@owc-utils/Mixins/EventListenerMixin';
import {
  isCarouselItemAVideo,
  transformCarouselToUnifiedFormat,
  resizeObserverCallback,
} from './MediaGalleryOverlayUtils';
import { MediaQueryService, WB_RESIZE_EVENT_NAME, Breakpoints } from '@owc-utils/MediaQueryService';
import {
  MEDIA_OVERLAY_THROTTLE_LIMIT,
  MEDIA_OVERLAY_CLOSE_EVENT_NAME,
  THUMBNAILS_FORCED_IMAGE_RATIO,
} from './MediaGalleryOverlay.config';
import Overlay from '@/packages/Overlay/src/Overlay.vue';
// Since there's no official workbench icon for this one, we had to import it manually.
// The designers put the icon on the specs so we could download it and use it.
// https://jira.mercedes-benz.io/browse/CINOWF-29584
import OverlayPinchIcon from '!!raw-loader!./MediaGalleryPinchIcon.svg';
import { MEDIA_GALLERY_DEBOUNCE_TIME } from '../mediaGallery.config';
import { CUSTOM_EVENTS as VIDEO_EVENTS } from '@owc-packages/VideoPlayer/src/VideoPlayer.config';

const DESKTOP_VIEWPORT = [Breakpoints.mq4, Breakpoints.mq5, Breakpoints.mq6];
const scrollLockService = new ScrollLockService();
const mediaQueryService = new MediaQueryService(window);

export default (Vue as VueConstructor<Vue & { $refs: MediaGalleryOverlayRefs }>).extend<
  MediaGalleryOverlayData,
  MediaGalleryOverlayMethods,
  MediaGalleryOverlayComputed,
  MediaGalleryOverlayProps
>({
  name: 'owc-media-gallery-overlay',
  mixins: [
    createEventListener('resize', 'checkContentOverlow', {
      throttleLimit: MEDIA_OVERLAY_THROTTLE_LIMIT,
    }),
  ],
  props: {
    isDarkMode: {
      type: Boolean,
      default: false,
    },
    galleryItems: {
      type: Array,
    },
    overlayPinchLabel: {
      type: String,
      required: true,
    },
    galleryItemIndex: {
      type: Number,
      default: 0,
    },
  },
  components: {
    MediaGalleryCarouselItemComponent,
    MediaGalleryCarouselThumbnails,
    Overlay,
  },
  data: () => ({
    closeButtonClass: '',
    roundButtonClass: '',
    isContentOverflowing: false,
    currentSlide: 0,
    isMaximized: false,
    isOverlayClosed: true,
    ratio: THUMBNAILS_FORCED_IMAGE_RATIO,
    OverlayPinchIcon,
    isCurrentVideoPlaying: false,
    currentVideoPlayButton: null,
    showCloseButton: true,
  }),
  computed: {
    currentItem() {
      return this.galleryItems[this.currentSlide];
    },
    maximizedSrc() {
      let src;
      if (this.currentItem && !isCarouselItemAVideo(this.currentItem)) {
        const item = transformCarouselToUnifiedFormat(this.currentItem);
        src = item.src;
      }
      return src;
    },
    isNavigationDotsSticky() {
      return !this.isContentOverflowing;
    },
    isFirstSlide() {
      return this.currentSlide === 0;
    },
    isLastSlide() {
      return this.galleryItems.length - 1 === this.currentSlide;
    },
    isScreenMaxVisible() {
      return !isCarouselItemAVideo(this.currentItem);
    },
  },
  watch: {
    isCurrentVideoPlaying(newValue) {
      const currentVideoItem = this.currentItem as MediaGalleryCarouselItem<MediaGalleryCarouselVideo>;
      // hide the button
      this.showCloseButton = !(
        newValue && // if video is playing
        // and it opens in a modal
        currentVideoItem.json.videoDetails.openInModal
      );
    },
    isMaximized(newValue) {
      const mediaGalleryOverlay = this.$refs.mediaGalleryOverlay;
      let left = 0;
      if (newValue) {
        const slide = this.$refs.overlayMaximize;
        const newWidth = (window.innerHeight / 9) * 16;
        slide.style.setProperty('width', `${newWidth}px`);
        const scrollPosition = newWidth / 2 - window.innerWidth / 2;
        left = scrollPosition;
      }
      this.$nextTick(() => {
        mediaGalleryOverlay.scrollTo(left, 0);
      });
    },
  },
  mounted() {
    this.currentSlide = this.galleryItemIndex;

    const overlaySlider = this.$refs.overlaySlider;
    const resizeObserver = new ResizeObserver(
      debounce(MEDIA_GALLERY_DEBOUNCE_TIME, resizeObserverCallback, false),
    );

    resizeObserver.observe(overlaySlider);
    window.addEventListener(WB_RESIZE_EVENT_NAME, this.resizeHandler as EventListener);
    mediaQueryService.observe(true);

    const currentMediaQuery = mediaQueryService.getCurrentMediaQuery();
    this.updateCloseButton(currentMediaQuery);
    this.checkContentOverlow();
    scrollLockService.lock();
    this.bindKeyboardKeys();

    this.$el.addEventListener(VIDEO_EVENTS.start, () => {
      this.isCurrentVideoPlaying = true;
    });
    this.$el.addEventListener(VIDEO_EVENTS.stop, () => {
      this.isCurrentVideoPlaying = false;
    });
  },
  beforeDestroy(): void {
    window.removeEventListener(WB_RESIZE_EVENT_NAME, this.resizeHandler as EventListener);
    scrollLockService.unlock();
    this.unbindKeyboardKeys();
  },
  methods: {
    stopVideo() {
      if (this.isCurrentVideoPlaying) {
        this.isCurrentVideoPlaying = false;
        this.clickPlayButton();
      }
    },
    clickPlayButton() {
      this.currentVideoPlayButton?.click();
    },
    setCurentVideoPlayer() {
      const content = this.$refs.overlayContent;
      const current = content?.querySelector('.owc-media-gallery-overlay__current');
      const playButton = current?.querySelector(
        '.owc-video-player__play-button, [data-plyr="play"]',
      ) as HTMLButtonElement;
      if (playButton) {
        this.currentVideoPlayButton = playButton;
      }
    },
    resizeHandler(resizeEvent: CustomEvent<MediaQueryChangeEvent>) {
      this.updateCloseButton(resizeEvent.detail.current);
    },
    updateCloseButton(mediaQuery: MediaQuery) {
      if (DESKTOP_VIEWPORT.includes(mediaQuery)) {
        this.roundButtonClass = '';
      } else {
        this.roundButtonClass = 'wb-round-button--solid-level-0';
      }
    },
    /**
     * Is checking if text element collides with the navigation dots
     * if this is the case `isContentOverflowing` is set to true.
     * Reason:
     * Navigation dots need to stay to the bottom and this is fine
     * for the most cases. In a case with a long title and descriptions
     * the content becomes scrollable and it doesn't look good with the
     * navigations dots drawn over the text. In this case it should stick
     * to the bottom of the text elements and not to the bottom of the screen.
     */
    checkContentOverlow(): void {
      const content = this.$refs.overlayContent;
      const navigationDots = this.$refs.overlayNavigationDots;

      if (content && navigationDots) {
        const contentRect = content.getBoundingClientRect();
        const navigationDotsRect = navigationDots.getBoundingClientRect();

        // checking if the bottom corner intersects the the navigation dots.
        this.isContentOverflowing =
          contentRect.bottom >= navigationDotsRect.top && content.clientHeight >= window.innerHeight;
      }
    },
    bindKeyboardKeys() {
      document.addEventListener('keydown', this.handleKeyboardKeys);
    },
    unbindKeyboardKeys() {
      document.removeEventListener('keydown', this.handleKeyboardKeys);
    },
    handleKeyboardKeys(keyboardEvent: KeyboardEvent) {
      switch (keyboardEvent.key) {
        case 'Escape':
          this.onCloseClick();
          break;
        case 'ArrowLeft':
          this.prevSlide();
          break;
        case 'ArrowRight':
          this.nextSlide();
          break;
        default:
          return;
      }
    },
    onBackClick() {
      this.isMaximized = false;
    },
    onCloseClick() {
      this.unbindKeyboardKeys();
      this.isMaximized = false;
      this.$emit(MEDIA_OVERLAY_CLOSE_EVENT_NAME);
    },
    toggleMaximize() {
      this.isMaximized = !this.isMaximized;
      this.isOverlayClosed = false;
    },
    overlayCloseHandler() {
      this.isOverlayClosed = true;
    },
    nextSlide() {
      if (this.$refs.overlaySlider) {
        this.$refs.overlaySlider.next();
      }
    },
    prevSlide() {
      if (this.$refs.overlaySlider) {
        this.$refs.overlaySlider.previous();
      }
    },
    onDotNavigationChanged(wbchangeEvent: CustomEvent) {
      const dotNavigationPositon = wbchangeEvent.detail - 1;
      if (dotNavigationPositon < this.currentSlide) {
        this.prevSlide();
      } else {
        this.nextSlide();
      }
      this.currentSlide = dotNavigationPositon;
    },
    carouselSlideChanged(wbchangeEvent: CustomEvent) {
      const carouselPosition = wbchangeEvent.detail - 1;
      if (carouselPosition !== this.currentSlide) {
        this.currentSlide = carouselPosition;
      }
      this.setCurentVideoPlayer();
      this.stopVideo();
    },
    updateCurrentItem(targetSlide: number) {
      this.$refs.overlaySlider.scrollToItem(targetSlide);
    },
  },
});
</script>
<style lang="scss">
@import '@owc-styles/utils/z-index-list';
@import '@owc-styles/utils/aspect-ratio';

@mixin mediaGalleryOverlayGrid {
  @include wb-grid-gaps;

  display: grid;
  grid-row-gap: var(--wb-grid-gutter-width);
  grid-template-columns: repeat(12, 1fr);
}

.owc-media-gallery-overlay {
  $image-top-margin-from-mq4: 16px;

  position: fixed;
  top: 0;
  left: 0;

  z-index: $z-index-overlay;

  width: 100%;
  height: 100vh;
  max-height: var(--window-height, 100vh);

  overflow-y: auto;

  background-color: var(--wb-white);

  &--dark {
    color: var(--wb-white);

    background-color: var(--wb-black);
  }
  &__wrapper {
    @include breakpoint-from(mq4) {
      @include wb-content-width;
      @include mediaGalleryOverlayGrid;
    }
  }
  &__content-wrapper {
    // this element is meant to wrap the 2nd line of the grid
    // and wraps both the slider's content and the controls
    position: relative;

    @include breakpoint-from(mq4) {
      @include mediaGalleryOverlayGrid;
      grid-row: 2;
      grid-column: 1 / span 12;
    }
  }
  &__content {
    // just the slides and the slides' captions
    position: relative;

    @include breakpoint-from(mq4) {
      grid-column: 3 / span 8;
      max-width: 100%;
    }
  }
  &__controls {
    display: none;

    @include breakpoint-from(mq4) {
      // make it a grid like the others so it will coincide
      // with the layout
      @include mediaGalleryOverlayGrid;
      // reset controls position to make sure
      // their placement is the same as the image's
      position: absolute;
      top: 0;
      left: 0;

      width: 100%;
    }
  }
  &__controls-mock-image {
    @include aspect-ratio(16, 9);

    grid-column: 3 / span 8;
  }
  &__dot-navigation {
    margin-bottom: var(--wb-spacing-s);

    @include breakpoint-from(mq4) {
      display: none;
    }

    &--stick-to-bottom {
      position: fixed;
      bottom: var(--wb-spacing-s);

      width: 100%;
      margin-bottom: 0;
    }
  }
  &__btn {
    position: fixed;
    z-index: $z-index-default;

    @include breakpoint-from(mq4) {
      position: relative;
      z-index: unset;

      justify-self: center;
    }

    &--screen-max {
      right: var(--wb-spacing-xxs);
      // 56.25% = 100% * 9/16 (16x9 image ratio)
      margin-top: calc(56.25% - var(--wb-spacing-xxs));

      // need to move the button by its own height
      transform: translateY(-100%);

      @include breakpoint-from(mq4) {
        //need to increase specificity to override wb display for round-button
        &.wb-round-button {
          display: none;
        }
      }
    }
    &--close,
    &--back {
      top: var(--wb-spacing-xxs);

      @include breakpoint-from(mq4) {
        top: auto;
      }
    }
    &--back {
      left: var(--wb-spacing-xxs);

      @include breakpoint-from(mq4) {
        left: auto;
      }
    }
    &--close {
      right: var(--wb-spacing-xxs);

      @include breakpoint-from(mq4) {
        right: auto;

        grid-row: 1;
        grid-column: 11;
      }
    }
    &--prev,
    &--next {
      display: none;

      @include breakpoint-from(mq4) {
        display: block;
        align-self: center;
      }
    }
    &--prev {
      grid-column: 2;
    }
    &--next {
      grid-column: 11;
    }
  }
  &__slider {
    // substract spacing for the dot navigation
    height: calc(100vh - var(--wb-spacing-m));
    height: calc(var(--window-height, 100vh) - var(--wb-spacing-m));

    @include breakpoint-from(mq4) {
      height: auto;
    }
  }
  &__thumbnails {
    --mediaGalleryRowHeight: 100%;

    @include breakpoint-from(mq4) {
      grid-row: 3;
      grid-column: 3 / span 8;
    }
  }
  &__zoomed-image {
    z-index: $z-index-negative;
  }
  &__overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }
  &__hint {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    height: var(--window-height, 100vh);

    color: var(--wb-grey-90);
  }
}
</style>
